import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../shared/API/apiClient";
import EntryDetailsCard from "../../shared/components/EntryDetailsCard";
import DynamicActivitySection from "../../shared/components/DynamicActivitySection";
import Toast from "../../shared/components/Toast";
import "../../styles/pages/record_entry_form.css";
import { useReportingYear } from "../../context/ReportingYearContext";
import usePermission from "../../shared/hooks/usePermission";

const RecordActivityPage = () => {

const { hasPermission } = usePermission();
const canCreate =
  hasPermission(
    "record_activity",
    "create"
  );
  
  // =========================================
  // NAVIGATION
  // =========================================

  const navigate = useNavigate();

  // =========================================
  // REPORTING YEAR
  // =========================================

  const {
    reportingYear,
    reportingYearId
  } = useReportingYear();
const [isYearLocked, setIsYearLocked] = useState(true);

useEffect(() => {

  const fetchYearLockStatus = async () => {

    try {

      const res =
        await apiClient.post(
          "/api/reportingyear"
        );

      const years =
        res.data?.data || [];

      const normalise = (val) =>
        (val ?? "")
          .toString()
          .trim()
          .replace(/^fy/i, "")
          .toLowerCase();

      const matched =
        years.find(
          (y) =>
            normalise(y.year_label) ===
            normalise(reportingYear)
        );

      setIsYearLocked(
        matched
          ? Number(matched.is_locked) === 1
          : true
      );

    } catch (err) {

      console.error(
        "REPORTING YEAR LOCK ERROR",
        err
      );

      // Fail closed
      setIsYearLocked(true);
    }
  };

  if (reportingYear) {
    fetchYearLockStatus();
  }

}, [reportingYear]);
  // =========================================
  // TOAST
  // =========================================

  const [toast, setToast] =
    useState(null);

  // =========================================
  // VALIDATION
  // =========================================

  const [errors, setErrors] =
    useState({});

  // =========================================
  // MASTER DATA
  // =========================================

  const [sites, setSites] =
    useState([]);

  const [
    activityTypes,
    setActivityTypes
  ] = useState([]);

  const [
    departments,
    setDepartments
  ] = useState([]);

  // =========================================
  // ACTIVITY CONFIG
  // =========================================

  const [
    activityConfig,
    setActivityConfig
  ] = useState(null);

  // =========================================
  // LOADING STATES
  // =========================================

  const [
    loadingSites,
    setLoadingSites
  ] = useState(false);

  const [
    loadingActivities,
    setLoadingActivities
  ] = useState(false);

  const [
    loadingDepartments,
    setLoadingDepartments
  ] = useState(false);

  const [
    loadingActivityConfig,
    setLoadingActivityConfig
  ] = useState(false);


  // =========================================
  // FORM STATE
  // =========================================

  const [form, setForm] =
    useState({

      reporting_year:
        reportingYear || "",

      location_id: "",
      location_name: "",

      legal_entity: "",

      department_id: "",

      activity_type_id: "",

      record_date: "",

      notes: "",

      evidence_files: [],

      entries: [
        {
          source_id: "",
          source_name: "",
          value: ""
        }
      ]
    });

  // =========================================
  // UPDATE REPORTING YEAR
  // =========================================

  useEffect(() => {

    setForm((prev) => ({
      ...prev,
      reporting_year:
        reportingYear || ""
    }));

  }, [reportingYear]);


  // =========================================
  // INITIAL LOAD
  // =========================================
  
 useEffect(() => {

  if (!reportingYearId)
    return;

  if (!canCreate)
    return;

  fetchSites();
  fetchActivityTypes();
  fetchDepartments();

}, [
  reportingYearId,
  canCreate
]);

  // =========================================
  // FETCH SITES
  // =========================================

  const fetchSites = async () => {

    try {

      setLoadingSites(true);

      const response =
        await apiClient.get(
          "/api/activity-sites",
          {
            params: {
              reporting_year_id:
                reportingYearId
            }
          }
        );

      const sitesData =
        response.data?.data?.data ||
        response.data?.data ||
        [];

      setSites(sitesData);

    } catch (err) {

      console.error(
        "SITES API ERROR",
        err
      );

    } finally {

      setLoadingSites(false);
    }
  };

  // =========================================
  // FETCH ACTIVITY TYPES
  // =========================================

  const fetchActivityTypes =
    async () => {

      try {

        setLoadingActivities(
          true
        );

        const response =
          await apiClient.get(
            "/api/activitytype",
            {
              params: {
                reporting_year_id:
                  reportingYearId
              }
            }
          );

        const activityData =
          response.data?.data?.data ||
          response.data?.data ||
          [];

        setActivityTypes(
          activityData
        );

      } catch (err) {

        console.error(
          "ACTIVITY TYPES ERROR",
          err
        );

      } finally {

        setLoadingActivities(
          false
        );
      }
    };

  // =========================================
  // FETCH DEPARTMENTS
  // =========================================

  const fetchDepartments =
    async () => {

      try {

        setLoadingDepartments(
          true
        );

        const response =
          await apiClient.get(
            "/api/energy-fiscal-period-list"
          );

        setDepartments(
          response.data?.data
            ?.departments?.data || []
        );

      } catch (err) {

        console.error(
          "DEPARTMENTS ERROR",
          err
        );

      } finally {

        setLoadingDepartments(
          false
        );
      }
    };

  // =========================================
  // LOCATION CHANGE
  // =========================================

  const handleLocationChange =
    (e) => {

      const locationId =
        e.target.value;

      const selectedLocation =
        sites.find(
          (site) =>
            String(site.site_id) ===
            String(locationId)
        );

      setForm((prev) => ({
        ...prev,

        location_id:
          locationId,

        location_name:
          selectedLocation?.site_name ||
          "",

        legal_entity:
          selectedLocation?.legal_entity ||
          selectedLocation?.entity_name ||
          selectedLocation?.legal_entity_name ||
          ""
      }));

      setErrors((prev) => ({
        ...prev,
        location_id: "",
        legal_entity: ""
      }));
    };

  // =========================================
  // ACTIVITY CHANGE
  // =========================================

  const handleActivityChange =
    async (e) => {

      const activityId =
        e.target.value;

      setForm((prev) => ({
        ...prev,

        activity_type_id:
          activityId,

        entries: [
          {
            source_id: "",
            source_name: "",
            value: ""
          }
        ]
      }));

      setActivityConfig(null);

      try {

        setLoadingActivityConfig(
          true
        );

        const response =
          await apiClient.get(
            `/api/get-activity-data?activitytype_id=${activityId}`
          );

        const config =
          response.data?.data?.[0] ||
          null;

        setActivityConfig(config);

      } catch (err) {

        console.error(
          "ACTIVITY CONFIG ERROR",
          err
        );

      } finally {

        setLoadingActivityConfig(
          false
        );
      }
    };

  // =========================================
  // VALIDATE FORM
  // =========================================

  const validateForm = () => {

    const newErrors = {};

    if (!form.location_id) {
      newErrors.location_id =
        "Location is required";
    }

    if (!form.legal_entity) {
      newErrors.legal_entity =
        "Legal Entity is required";
    }

    if (!form.record_date) {
      newErrors.record_date =
        "Date is required";
    }

    if (
      !form.activity_type_id
    ) {
      newErrors.activity_type_id =
        "Activity Type is required";
    }

    form.entries.forEach(
      (row, index) => {

        if (!row.source_id) {

          newErrors[
            `source_${index}`
          ] =
            "Source is required";
        }

        if (
          !row.value ||
          Number(row.value) <= 0
        ) {

          newErrors[
            `value_${index}`
          ] =
            "Value is required";
        }
      }
    );

    setErrors(newErrors);

    return (
      Object.keys(newErrors)
        .length === 0
    );
  };

  // =========================================
  // GENERATE PAYLOAD
  // =========================================

 const generatePayload = (
  status = "submitted",
  isDraft = 0
) => {

  // =====================================
  // CONFIG PARSING
  // =====================================

  const sourceDetails =
    activityConfig?.source_field_details
      ? JSON.parse(
          activityConfig.source_field_details
        )
      : null;

  const disposalDetails =
    activityConfig?.disposal_field_details
      ? JSON.parse(
          activityConfig.disposal_field_details
        )
      : null;

  const emissionDetails =
    activityConfig?.emission_factor_details
      ? JSON.parse(
          activityConfig.emission_factor_details
        )
      : null;

  // =====================================
  // FLAGS
  // =====================================

  const isMonitoringActivity =
    Number(
      activityConfig?.requires_parameters
    ) === 1;

  // =====================================
  // ACTIVE FIELD
  // =====================================

  const activeField =
    sourceDetails ||
    disposalDetails;

  // =====================================
  // FIELD TYPE
  // =====================================

  let fieldType = "source";

  if (disposalDetails) {
    fieldType =
      "disposal_method";
  }

  if (isMonitoringActivity) {
    fieldType = "parameter";
  }

  // =====================================
  // TOTALS
  // =====================================

  let totalConsumption = 0;

  let totalEmission = 0;

  // =====================================
  // ENTRIES
  // =====================================

  const entries =
    form.entries.map((row) => {

      const selectedOption =
        activeField?.options?.find(
          (o) =>
            String(o.option_id) ===
            String(row.source_id)
        );

      // ===================================
      // EMISSION
      // ===================================

      const activityEmissionEnabled =
        Number(
          activityConfig?.is_emission_applicable
        ) === 1;

      const optionRequiresEmission =
        Number(
          selectedOption?.is_emission_factor_required
        ) === 1;

      const emissionApplicable =
        !isMonitoringActivity &&
        activityEmissionEnabled &&
        optionRequiresEmission;

      const factorValue =
        emissionApplicable
          ? Number(
              emissionDetails?.factor_value || 0
            )
          : 0;

      const estimatedEmission =
        (
          Number(row.value || 0) *
          factorValue
        ) / 1000;

      // ===================================
      // TOTALS
      // ===================================

      totalConsumption +=
        Number(row.value || 0);

      totalEmission +=
        estimatedEmission;

      // ===================================
      // ENTRY
      // ===================================

      return {

        field_type:
          fieldType,

        field_label:
          activeField?.field_label || "",

        option_id:
          selectedOption?.option_id || null,

        option_label:
          selectedOption?.option_value || "",

        value:
          Number(row.value || 0),

        unit:
          activityConfig?.unit_name || "",

        emission: {

          is_applicable:
            emissionApplicable,

          factor:
            factorValue,

          factor_unit:
            emissionApplicable
              ? emissionDetails?.emission_factor_unit
              : "",

          value:
            Number(
              estimatedEmission.toFixed(2)
            )
        },

        // =================================
        // PARAMETERS
        // =================================

        parameters:
          isMonitoringActivity
            ? (
                row.parameters || []
              )
            : [],

        metadata: {}
      };
    });

  // =====================================
  // FINAL PAYLOAD
  // =====================================

  return {

    reporting_year_id:
      reportingYearId,

    location_id:
      form.location_id,

    department_id:
      form.department_id || "",

    activity_type_id:
      form.activity_type_id,

    entry_date:
      form.record_date,

    month:
      form.record_date
        ? form.record_date.split("-")[1]
        : "",

    legal_entity:
      form.legal_entity || "",

    status:
      status,

    is_draft:
      isDraft,

    notes:
      form.notes || "",

    summary: {

      total_entries:
        entries.length,

      total_value:
        Number(
          totalConsumption
        ),

      unit:
        activityConfig?.unit_name || "",

      total_emission:
        Number(
          totalEmission.toFixed(2)
        )
    },

    entries
  };
};

  // =========================================
  // SAVE DATA
  // =========================================

  const saveActivity =
    async (
      status = "submitted",
      isDraft = 0
    ) => {

      try {

        const payload =
          generatePayload(
            status,
            isDraft
          );

        const formData =
          new FormData();

        Object.keys(payload)
          .forEach((key) => {

            const value =
              payload[key];

            if (
              typeof value ===
                "object" &&
              value !== null
            ) {

              formData.append(
                key,
                JSON.stringify(
                  value
                )
              );

            } else {

              formData.append(
                key,
                value
              );
            }
          });

       if (form.evidence_files?.length > 0) 
       {
        form.evidence_files.forEach((file) => {
          formData.append("evidence_files[]",file);
    }
  );
}

        const response =
          await apiClient.post(
            "/api/save-activity-data",
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data"
              }
            }
          );

        setToast({
          type: "success",
          message:
            response.data
              ?.message ||
            (
              isDraft
                ? "Draft saved successfully"
                : "Activity submitted successfully"
            )
        });

        setTimeout(() => {

          navigate(
            "/dashboard/energy-consumption"
          );

        }, 1500);

      } catch (err) {

        console.error(
          "SAVE ACTIVITY ERROR",
          err.response?.data ||
            err
        );

        setToast({
          type: "error",
          message:
            err?.response?.data
              ?.message ||
            "Something went wrong"
        });
      }
    };

  // =========================================
  // SAVE DRAFT
  // =========================================

  const handleSaveDraft =
    async () => {

      await saveActivity(
        "draft",
        1
      );
    };

  // =========================================
  // SUBMIT
  // =========================================

  const handleSubmit =
    async () => {

      const isValid =
        validateForm();

      if (!isValid) {
        return;
      }

      await saveActivity(
        "submitted",
        0
      );
    };

if (isYearLocked) {
  return (
    <div className="dash-page">
      <div className="empty-entity">
        <div className="empty-entity__icon">
          <i className="fa-solid fa-lock" />
        </div>

        <div className="empty-entity__title">
          Reporting Year Closed
        </div>

        <div className="empty-entity__sub">
          Reporting year {reportingYear} is closed.
          You cannot record activities for this year.
        </div>
      </div>
    </div>
  );
}

if (!canCreate) {
  return (
    <div className="dash-page">
      <div className="empty-entity">
        <div className="empty-entity__icon">
          <i className="fa-solid fa-lock" />
        </div>

        <div className="empty-entity__title">
          Access Restricted
        </div>

        <div className="empty-entity__sub">
          You do not have permission to record activities.
        </div>
      </div>
    </div>
  );
}
  return (

    <>

  <div className="dash-page">

        {/* HEADER */}


                            <div className="dash-page-header">

                              <div>

                                <div className="dash-page-subtitle">
                                  ENVIRONMENT & CARBON
                                </div>

                                <div className="dash-page-title">
                                  Record Activity
                                </div>

                                <div className="dash-page-description">
                                  Record energy,
                                  fuel, water,
                                  waste and
                                  monitoring data.
                                </div>

                              </div>
                                  <button
                              className="org-btn-save"
                              onClick={() =>
                                navigate(
                                  "/dashboard/energy-consumption"
                                )
                              }
                            >
                              ← Back to Records
                            </button>

                            </div>

                            {/* ENTRY DETAILS */}

                            <EntryDetailsCard
                              form={form}
                              setForm={setForm}
                              errors={errors}
                              setErrors={setErrors}
                              sites={sites}
                              departments={departments}
                              activityTypes={activityTypes}
                              loadingSites={loadingSites}
                              loadingActivities={loadingActivities}
                              loadingDepartments={loadingDepartments}
                              reportingYear={reportingYear}
                              onLocationChange={handleLocationChange}
                              onActivityChange={handleActivityChange}
                            />

                            {/* DYNAMIC SECTION */}

                            {activityConfig && (

                              <DynamicActivitySection
                                activityConfig={activityConfig}
                                form={form}
                                setForm={setForm}
                                errors={errors}
                                setErrors={setErrors}
                                loading={loadingActivityConfig}
                              />

                            )}

                            {/* NOTES & EVIDENCE */}

                            <div className="le-card ecf-card">

                              <div className="ecf-notes-evidence-grid">

                                {/* NOTES */}

                                <div className="org-form-field">

                                  <div className="ecf-field-label-row">

                                    <label className="org-field-label">
                                      Notes
                                    </label>

                                    <span className="ecf-label-tag">
                                      Optional
                                    </span>

                                  </div>

                                  <div className="ecf-notes-wrapper">

                                    <textarea
                                      className="org-field-input ecf-notes-textarea"
                                      placeholder="Add any context, assumptions, or remarks..."
                                      maxLength={1000}
                                      value={
                                        form.notes || ""
                                      }
                                      onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        notes: e.target.value
                      }))
                    }
                />

                <div className="ecf-char-count">
                  {
                    (
                      form.notes ||
                      ""
                    ).length
                  }/1000
                </div>

              </div>

            </div>

            {/* EVIDENCE */}

            <div className="org-form-field">

              <div className="ecf-field-label-row">

                <label className="org-field-label">
                  Evidence
                </label>

                <span className="ecf-label-tag">
                  Optional
                </span>

              </div>

              <label className="ecf-upload-box">

                <input
                  type="file" multiple
                  className="ecf-file-input"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx"
                  onChange={(e) => {

                      const files =
                        Array.from(
                          e.target.files || []
                        );

                      setForm((prev) => ({

                        ...prev,

                        evidence_files: [
                          ...(prev.evidence_files || []),
                          ...files
                        ]

                      }));
                    }}
                />

                <div className="ecf-upload-inner">

                  <span className="ecf-upload-icon">
                    ⬆️
                  </span>

                  <span className="ecf-upload-title">
                    Upload File
                  </span>

                  <span className="ecf-upload-subtitle">
                    PDF, JPG, PNG,
                    XLSX · Max 10MB
                  </span>

                </div>

              </label>

            {
            form.evidence_files?.length > 0 && (

              <div
                className="ecf-upload-files-list"
                style={{
                  marginTop: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10
                }}
              >

      {form.evidence_files.map(
        (file, index) => (

          <div
            key={index}
            className="ecf-upload-file-name"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12
            }}
          >

            {/* FILE NAME */}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >

              <span>📎</span>

              <span>
                {file.name}
              </span>

            </div>

            {/* REMOVE */}

            <button
              type="button"
              className="ecf-remove-btn"
              onClick={() => {

                setForm((prev) => ({

                  ...prev,

                  evidence_files:
                    prev.evidence_files.filter(
                      (_, i) => i !== index
                    )

                }));
              }}
            >
              ✕
            </button>

          </div>

        )
      )}

</div>

        )
      }

            </div>

          </div>

        </div>

        {/* FOOTER */}

        <div className="ecf-footer">

          <button
            type="button"
            className="org-btn-cancel"
            onClick={
              handleSaveDraft
            }
          >
            Save as Draft
          </button>

          <button
            type="button"
            className="org-btn-save"
            onClick={
              handleSubmit
            }
          >
            Submit
          </button>

        </div>

      </div>

      {/* TOAST */}

      {toast && (

        <Toast
          message={
            toast.message
          }
          type={toast.type}
          onClose={() =>
            setToast(null)
          }
        />

      )}

    </>

  );
};

export default RecordActivityPage;